import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { authedFetch } from '../api'

export interface Run {
  id: string
  name: string
  agent_id: string
  agent_name: string
  agent_names: string[]
  attempts_count: number
  refined: boolean
  selected_tags: string[]
  selected_failures: string[]
  success_rate: number | null
  avg_cost: number | null
  status: string
  created_at: string
}

interface RunsState {
  items: Run[]
  loading: boolean
  error: string | null
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

const initialState: RunsState = {
  items: [],
  loading: false,
  error: null,
  total: 0,
  page: 0,
  pageSize: 50,
  hasMore: true,
}

export const fetchRuns = createAsyncThunk('runs/fetchRuns', async () => {
  const response = await fetch('/api/runs?page=1&pageSize=50')
  if (!response.ok) throw new Error('Failed to fetch runs')
  return response.json() as Promise<{ items: Run[]; total: number; page: number; pageSize: number }>
})

export const fetchMoreRuns = createAsyncThunk('runs/fetchMoreRuns', async (page: number) => {
  const response = await fetch(`/api/runs?page=${page}&pageSize=50`)
  if (!response.ok) throw new Error('Failed to fetch runs')
  return response.json() as Promise<{ items: Run[]; total: number; page: number; pageSize: number }>
})

export const createRun = createAsyncThunk(
  'runs/createRun',
  async (run: { agent_ids: string[]; selected_tags: string[]; selected_failures: string[]; attempts_count: number; refined: boolean }) => {
    const response = await authedFetch('/api/runs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(run),
    })
    if (!response.ok) throw new Error('Failed to create run')
    const created = await response.json() as Run

    // Trigger container creation — start-run stores specs in DB and triggers per-container spawns
    const startRes = await authedFetch('/api/start-run', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ run_id: created.id }),
    })
    if (!startRes.ok) {
      const errData = await startRes.json().catch(() => ({ error: 'Failed to start run' }))
      console.error('start-run error:', errData.error)
    }

    return created
  }
)

export const deleteRun = createAsyncThunk(
  'runs/deleteRun',
  async (id: string) => {
    const response = await authedFetch(`/api/runs/${id}`, {
      method: 'DELETE',
    })
    if (!response.ok) throw new Error('Failed to delete run')
    return id
  }
)

const runsSlice = createSlice({
  name: 'runs',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRuns.pending, (state) => {
        if (state.items.length === 0) state.loading = true
        state.error = null
      })
      .addCase(fetchRuns.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.items
        state.total = action.payload.total
        state.page = action.payload.page
        state.pageSize = action.payload.pageSize
        state.hasMore = action.payload.page * action.payload.pageSize < action.payload.total
      })
      .addCase(fetchRuns.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch runs'
      })
      .addCase(fetchMoreRuns.fulfilled, (state, action) => {
        state.items = [...state.items, ...action.payload.items]
        state.total = action.payload.total
        state.page = action.payload.page
        state.pageSize = action.payload.pageSize
        state.hasMore = action.payload.page * action.payload.pageSize < action.payload.total
      })
      .addCase(createRun.fulfilled, (state, action) => {
        state.items.unshift(action.payload)
        state.total += 1
      })
      .addCase(deleteRun.fulfilled, (state, action) => {
        state.items = state.items.filter((r) => r.id !== action.payload)
        state.total -= 1
      })
  },
})

export default runsSlice.reducer
